import React, { useState, useRef, useEffect } from 'react';
import { Send, Coffee, User, Loader2, Sparkles, PlusCircle, Zap } from 'lucide-react';
import { getOpenAIAppIdeaConsultation } from '../services/openaiService.ts';
import ReactMarkdown from 'react-markdown';

const COMMON_PROBLEMS = [
  "Tracking retail stock",
  "Coordinating orders",
  "Managing bookings",
  "Automating deliveries",
  "Inventory management",
  "Chasing invoices",
  "No credible website for bigger deals"
];

const AIConsultant: React.FC = () => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<{ role: 'user' | 'model', text: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const processSubmission = async (text: string) => {
    if (!text.trim() || loading) return;

    const userMsg = text.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setLoading(true);

    const aiResponse = await getOpenAIAppIdeaConsultation(userMsg);
    
    setMessages(prev => [...prev, { role: 'model', text: aiResponse }]);
    setLoading(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    processSubmission(input);
  };

  const handleSuggestionClick = (problem: string) => {
    processSubmission(problem);
  };

  return (
    <section id="guide" className="py-20 sm:py-32 bg-[#1F2933] px-4 sm:px-6">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#7FAF9A]/20 text-[#7FAF9A] text-[10px] sm:text-xs font-bold uppercase tracking-wider mb-4 sm:mb-6">
            <Sparkles size={14} />
            <span>The Tiny Strategist</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#F4F1EC] mb-4 sm:mb-6 tracking-tight">The Concept Lab.</h2>
          <p className="text-[#F4F1EC]/60 text-base sm:text-lg max-w-xl mx-auto font-medium">Describe a daily chore. Our guide suggests a tiny tool to help you reclaim your time.</p>
        </div>

        <div className="bg-[#F4F1EC] rounded-[2rem] sm:rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col h-[550px] sm:h-[700px] border border-white/10">
          {/* Header */}
          <div className="bg-white/50 p-4 sm:p-6 border-b border-[#1F2933]/5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-9 h-9 sm:w-12 sm:h-12 bg-[#7FAF9A] rounded-xl sm:rounded-2xl flex items-center justify-center text-white shadow-sm flex-shrink-0">
                <Coffee size={20} className="sm:w-6 sm:h-6" />
              </div>
              <div>
                <p className="font-bold text-[#1F2933] text-sm sm:text-lg leading-tight">Your Calm Guide</p>
                <p className="text-[9px] sm:text-[10px] text-[#7FAF9A] font-black uppercase tracking-widest">Always Listening</p>
              </div>
            </div>
            
            <div className="hidden xs:flex bg-[#7FAF9A]/10 px-2 sm:px-3 py-1.5 rounded-lg sm:rounded-xl items-center gap-1.5 sm:gap-2 flex-shrink-0 border border-[#7FAF9A]/20">
              <Zap size={12} className="text-[#7FAF9A] sm:w-[14px]" />
              <span className="text-[8px] sm:text-[9px] font-black uppercase tracking-[0.1em] text-[#7FAF9A] whitespace-nowrap">OpenAI Powered</span>
            </div>
          </div>

          {/* Messages Area */}
          <div 
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 sm:p-8 space-y-6 sm:space-y-8 scroll-smooth"
          >
            {messages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center px-4 text-[#1F2933]/30">
                <Coffee size={40} className="mb-4 sm:mb-6 opacity-10" />
                <p className="text-base sm:text-lg font-bold max-w-sm leading-relaxed">What task drains your energy? I'll suggest a tiny system to fix it.</p>
                <div className="mt-4 flex items-center gap-2 text-[10px] font-black bg-white/40 px-3 py-1.5 rounded-full uppercase tracking-widest">
                  <Zap size={14} className="text-[#7FAF9A]" />
                  Secure Cloud
                </div>
              </div>
            )}
            
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex gap-3 sm:gap-4 max-w-[95%] sm:max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                  <div className={`w-7 h-7 sm:w-9 sm:h-9 rounded-lg sm:rounded-xl flex-shrink-0 flex items-center justify-center ${
                    msg.role === 'user' ? 'bg-[#7FAF9A]/10 text-[#7FAF9A]' : 'bg-white text-[#1F2933]'
                  }`}>
                    {msg.role === 'user' ? <User size={14} className="sm:w-[18px]" /> : <Coffee size={14} className="sm:w-[18px]" />}
                  </div>
                  <div className={`p-3.5 sm:p-6 rounded-[1.25rem] sm:rounded-[1.75rem] text-sm sm:text-lg ${
                    msg.role === 'user' 
                      ? 'bg-[#7FAF9A] text-white rounded-tr-none shadow-sm' 
                      : 'bg-white text-[#1F2933] border border-[#1F2933]/5 rounded-tl-none prose prose-slate max-w-none shadow-sm'
                  }`}>
                    {msg.role === 'model' ? (
                      <div className="prose prose-sm sm:prose-base prose-slate max-w-none text-[#1F2933]/90 leading-relaxed font-medium">
                        <ReactMarkdown>{msg.text}</ReactMarkdown>
                      </div>
                    ) : (
                      <p className="font-semibold">{msg.text}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {loading && (
              <div className="flex justify-start animate-pulse">
                <div className="flex gap-3 sm:gap-4 items-center text-[#1F2933]/40">
                  <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-lg sm:rounded-xl bg-white flex items-center justify-center">
                    <Loader2 size={16} className="animate-spin sm:w-[18px]" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest italic">
                    Consulting Strategist...
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Input & Suggestions Footer */}
          <div className="p-4 sm:p-6 bg-white border-t border-[#1F2933]/5 mt-auto">
            <div className="flex overflow-x-auto pb-3 mb-3 sm:mb-4 sm:flex-wrap sm:overflow-visible gap-2 no-scrollbar scroll-smooth">
              {COMMON_PROBLEMS.map((problem, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSuggestionClick(problem)}
                  disabled={loading}
                  className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-[#F4F1EC] text-[#1F2933]/70 text-[10px] sm:text-xs font-black border border-[#1F2933]/5 hover:bg-[#7FAF9A]/10 hover:text-[#7FAF9A] hover:border-[#7FAF9A]/20 transition-all flex items-center gap-1.5 sm:gap-2 group whitespace-nowrap shrink-0"
                >
                  <PlusCircle size={12} className="opacity-40 group-hover:opacity-100 sm:w-[14px]" />
                  {problem}
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="flex gap-2 sm:gap-3">
              <input 
                type="text" 
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="How can we help today?"
                className="flex-1 px-4 sm:px-6 py-3 sm:py-4 rounded-xl sm:rounded-2xl border border-[#1F2933]/10 focus:outline-none focus:ring-2 focus:ring-[#7FAF9A] focus:border-transparent text-[#1F2933] font-bold text-sm sm:text-base bg-gray-50/50"
                disabled={loading}
              />
              <button 
                type="submit"
                disabled={loading || !input.trim()}
                className="bg-[#7FAF9A] text-white p-3 sm:p-4 rounded-xl sm:rounded-2xl hover:bg-[#6b9a87] transition-all shadow-md disabled:bg-gray-200 flex-shrink-0"
              >
                <Send size={18} className="sm:w-6 sm:h-6" />
              </button>
            </form>
            <p className="text-[8px] sm:text-[9px] text-[#1F2933]/30 mt-3 sm:mt-4 text-center font-black uppercase tracking-[0.25em]">
              Authorized Cloud Connection
            </p>
          </div>
        </div>
      </div>
      <style dangerouslySetInnerHTML={{ __html: `
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        @media (max-width: 480px) {
          .xs\\:flex { display: flex; }
          .xs\\:hidden { display: none; }
        }
      `}} />
    </section>
  );
};

export default AIConsultant;
